/**
 * API Configuration
 * 
 * This file contains all API-related configuration settings.
 * Use this as the single source of truth for API URLs and versioning.
 */

// Environment-based API URL
export const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001';

// API Version - Change this when upgrading API versions
export const API_VERSION = 'v1';

// Base URL for all API endpoints
export const API_BASE = `${API_URL}/api/${API_VERSION}`;

/**
 * API Endpoints Configuration
 * 
 * All endpoint paths are relative to API_BASE.
 * Use these constants instead of hardcoding paths.
 */
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    register: '/auth/register',
    user: '/auth/user',
    profile: '/auth/profile',
    changePassword: '/auth/change-password',
    requestPasswordReset: '/auth/request-password-reset',
    resetPassword: '/auth/reset-password',
    requestVerification: '/auth/request-verification',
    verifyEmail: (token: string) => `/auth/verify-email/${token}`,
    csrfToken: '/auth/csrf-token',
    refreshToken: '/auth/refresh-token',
  },

  // User settings
  user: {
    settings: '/user/settings',
    export: '/user/export',
    account: '/user/account',
  },

  // Organizations
  organizations: {
    list: '/organizations',
    create: '/organizations',
    get: (id: string) => `/organizations/${id}`,
    update: (id: string) => `/organizations/${id}`,
    delete: (id: string) => `/organizations/${id}`,
    members: (id: string) => `/organizations/${id}/members`,
  },

  // Projects
  projects: {
    list: '/projects',
    create: '/projects',
    get: (id: string) => `/projects/${id}`,
    update: (id: string) => `/projects/${id}`,
    delete: (id: string) => `/projects/${id}`,
    byOrg: (orgId: string) => `/organizations/${orgId}/projects`,
  },

  // Assets
  assets: {
    list: '/assets',
    create: '/assets',
    get: (id: string) => `/assets/${id}`,
    update: (id: string) => `/assets/${id}`,
    delete: (id: string) => `/assets/${id}`,
    byProject: (projectId: string) => `/projects/${projectId}/assets`,
  },

  // Scanning
  scan: {
    trigger: (projectId: string) => `/scan/${projectId}`,
    status: (scanId: string) => `/scan/status/${scanId}`,
    results: (scanId: string) => `/scan/results/${scanId}`,
  },

  // Reports
  reports: {
    generate: '/reports/generate',
    list: '/reports',
    get: (id: string) => `/reports/${id}`,
    download: (id: string) => `/reports/${id}/download`,
  },

  // EU Alternatives (public endpoints)
  alternatives: {
    list: '/alternatives',
    categories: '/alternatives/categories',
    get: (id: string) => `/alternatives/${id}`,
    byCategory: (category: string) => `/alternatives/by-category/${category}`,
    click: (id: string) => `/alternatives/${id}/click`,
    stats: '/alternatives/stats',
  },

  // Submissions
  submissions: {
    list: '/submissions',
    create: '/submissions',
    approve: (id: string) => `/submissions/${id}/approve`,
    reject: (id: string) => `/submissions/${id}/reject`,
  },

  // API Tokens
  tokens: {
    list: '/tokens',
    create: '/tokens',
    get: (id: string) => `/tokens/${id}`,
    revoke: (id: string) => `/tokens/${id}`,
    refresh: (id: string) => `/tokens/${id}/refresh`,
  },

  // Subscriptions
  subscriptions: {
    current: '/subscriptions/current',
    plans: '/subscriptions/plans',
    checkout: '/subscriptions/checkout',
    portal: '/subscriptions/portal',
    usage: '/subscriptions/usage',
  },

  // Audit logs
  audit: {
    list: '/audit',
    byOrg: (orgId: string) => `/audit/organization/${orgId}`,
    adminAll: '/audit/admin/all',
  },

  // Documents
  documents: {
    list: '/documents',
    create: '/documents',
    get: (id: string) => `/documents/${id}`,
    byOrg: (orgId: string) => `/organizations/${orgId}/documents`,
  },

  // Compliance
  compliance: {
    questionnaire: '/questionnaires',
    questionnaireByOrg: (orgId: string) => `/organizations/${orgId}/questionnaire`,
    doraAssessment: (orgId: string) => `/organizations/${orgId}/compliance/dora`,
    nis2Assessment: (orgId: string) => `/organizations/${orgId}/compliance/nis2`,
    eprivacy: '/eprivacy',
    aiAct: '/aiact',
  },

  // Admin
  admin: {
    stats: '/admin/stats',
    users: '/admin/users',
    subscriptions: '/admin/subscriptions',
    complianceStats: '/admin/compliance-stats',
    toggleAdmin: (userId: string) => `/admin/users/${userId}/toggle-admin`,
    deleteUser: (userId: string) => `/admin/users/${userId}`,
  },

  // Apps & Integrations
  apps: {
    list: '/apps',
    byOrg: (orgId: string) => `/apps/organizations/${orgId}`,
    install: (orgId: string, appId: string) => `/apps/organizations/${orgId}/${appId}/install`,
    disable: (orgId: string, appId: string) => `/apps/organizations/${orgId}/${appId}/disable`,
    config: (orgId: string, appId: string) => `/apps/organizations/${orgId}/${appId}/config`,
  },

  // Metrics & Health (internal)
  metrics: {
    health: '/health',
    metrics: '/metrics',
  },
} as const;

/**
 * Helper to build full API URL
 */
export function buildApiUrl(endpoint: string): string {
  return `${API_BASE}${endpoint}`;
}

/**
 * Public endpoints that don't require authentication
 */
export const PUBLIC_ENDPOINTS = [
  API_ENDPOINTS.alternatives.list,
  API_ENDPOINTS.alternatives.categories,
  API_ENDPOINTS.alternatives.byCategory(''),
  API_ENDPOINTS.auth.login,
  API_ENDPOINTS.auth.register,
  API_ENDPOINTS.auth.requestPasswordReset,
  API_ENDPOINTS.auth.resetPassword,
  API_ENDPOINTS.submissions.create,
] as const;

/**
 * Routes that require authentication
 */
export const PROTECTED_ROUTES = [
  '/settings',
  '/account',
  '/dashboard',
  '/organizations',
  '/projects',
  '/apps',
  '/admin',
  '/audit',
] as const;

/**
 * Routes that are public (no auth required)
 */
export const PUBLIC_ROUTES = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/alternatives',
  '/pricing',
  '/about',
  '/blog',
  '/api-docs',
  '/terms',
  '/privacy',
  '/cookies',
  '/submit-company',
] as const;
